/**
 * 订单转派页 — 业务逻辑
 *
 * 【路由入参】
 * - URL 查询：`?orderId=xxx`（与订单详情跳转时保持一致，便于刷新后仍知订单）
 * - `location.state`：订单详情 `history.push` 传入 `{ orderId, orderNo, materialRows, type }`
 *
 * 【缓存方案】（与 adddeliveryorder 一致）
 * - 不使用 KeepAlive（路由无 cache: true）
 * - 使用 Redux pagecache 持久化页面状态
 * - 有 location.state → 使用并写入 Redux；无 location.state → 从 Redux 恢复
 *
 * 【供应商表单项】
 * - 由页面 `Form` 托管 `supplierId`、`supplierName`；提交接口从表单取值
 * - 校验文案：空 →「请输入供应商全称」；有搜索无精确命中 →「未找到匹配的供应商，请输入准确的全称」
 *
 * 【可转派数量】
 * - 公式：数量(num) − 取消数(cancelCount) − 退货数(reCount)，与订单详情页一致
 */
import { message } from 'antd'
import type { FormInstance } from 'antd/es/form'
import type { Rule } from 'antd/es/form'
import type { MutableRefObject } from 'react'
import {
  useCallback,
  useEffect,
  useReducer,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useDispatch } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { useTypedSelector } from '@/redux/store'
import { delModuleDataAction, setModuleDataAction } from '@/modules/pagecache'
import { useCloseTab, useQuery } from '@/tools/utilHooks'
import {
  getItemFromItemListByProperty,
  isResponseCodeSuccess,
} from '@/tools/util'
import reducer, {
  initialState,
  reloadStateAction,
  resetStateAction,
  setMaterialListAction,
  setOrderIdAction,
  setOrderNoAction,
  setSelectedMaterialRowKeysAction,
  TransferMaterialRow,
} from './reducer'
import {
  GET_SUPPLIER_CAN_TRANSFER,
  POST_TRANSFER_DISPATCH,
  type SupplierFindByDTO,
} from '@/api/orderManage'
import { debounce } from 'lodash'

// ---------- 纯函数：可转派数量 = 数量(num) − 取消数(cancelCount) − 退货数(reCount) ----------

/** 可转派数量 = num − cancelCount − reCount（与订单详情 hooks 一致） */
function getProductTransferRemainQty(row: {
  num?: number
  cancelCount?: number
  reCount?: number
}): number {
  const n = Number(row?.num ?? 0)
  const cancel = Number(row?.cancelCount ?? 0)
  const ret = Number(row?.reCount ?? 0)
  if ([n, cancel, ret].some((v) => Number.isNaN(v))) {
    return 0
  }
  return n - cancel - ret
}

/**
 * 将详情页勾选行 / 缓存行转为表格行。
 * 可转派数量默认 = num − cancelCount − reCount；旧缓存仅有 transferableQty 且无三字段时沿用该值。
 */
function normalizeMaterialRows(rows: any[]): TransferMaterialRow[] {
  return (rows || []).map((row) => {
    const num =
      row?.num !== undefined && row?.num !== null ? Number(row.num) : undefined
    const cancelCount =
      row?.cancelCount !== undefined && row?.cancelCount !== null
        ? Number(row.cancelCount)
        : undefined
    const reCount =
      row?.reCount !== undefined && row?.reCount !== null
        ? Number(row.reCount)
        : undefined
    const hasLineQty =
      num !== undefined || cancelCount !== undefined || reCount !== undefined
    const transferableQty = hasLineQty
      ? getProductTransferRemainQty({ num, cancelCount, reCount })
      : row?.transferableQty != null
        ? Number(row.transferableQty)
        : 0
    return {
      id: String(row?.id ?? ''),
      code: String(row?.code ?? ''),
      platformMaterialCode: row?.platformMaterialCode,
      systemCategoryCode: row?.systemCategoryCode,
      brand: row?.brand,
      name: row?.name,
      model: row?.model,
      unit: row?.unit,
      num,
      cancelCount,
      reCount,
      transferableQty,
    }
  })
}

const FORM_SUPPLIER_ID = 'supplierId'
const FORM_SUPPLIER_NAME = 'supplierName'

/** Redux 页缓存 key */
const CACHE_KEY = 'ordertransfertemp'

/** 供应商 Select 远程搜索防抖（ms），减轻 /supplier/findBy 压力 */
const SUPPLIER_SEARCH_DEBOUNCE_MS = 500

/** Redux 页缓存里与本页对应的 data 形状 */
type OrderTransferCacheData = {
  orderId?: string
  orderNo?: string
  materialList?: TransferMaterialRow[]
  selectedMaterialRowKeys?: React.Key[]
  type?: string
  orderTransferSupplier?: { supplierId?: string; supplierName?: string }
}

/** 清空表单与搜索态 */
function resetSupplierPickState(
  f: FormInstance | undefined,
  metaRef: MutableRefObject<{ keyword: string; noExactMatch: boolean }>,
  setOptions: (o: { label: string; value: string }[]) => void,
) {
  f?.setFieldsValue({
    [FORM_SUPPLIER_ID]: undefined,
    [FORM_SUPPLIER_NAME]: '',
  })
  metaRef.current = { keyword: '', noExactMatch: false }
  setOptions([])
}

function mapSupplierFindByRow(
  item: SupplierFindByDTO,
): { label: string; value: string } | null {
  const value = String(item.id ?? '').trim()
  const label = String(item.name ?? '').trim()
  if (!value || !label) return null
  return { label, value }
}

/** 接口 `data` 可能是单条 `{ id, name }` 或 `SupplierFindByDTO[]` */
function normalizeSupplierFindByList(data: unknown): SupplierFindByDTO[] {
  if (data == null) return []
  if (Array.isArray(data)) return data as SupplierFindByDTO[]
  if (typeof data === 'object' && data !== null) {
    const o = data as Record<string, unknown>
    if ('id' in o && 'name' in o) {
      return [data as SupplierFindByDTO]
    }
  }
  return []
}

// ---------- Hook 主体 ----------

export default function useOrderTransferHook(form?: FormInstance) {
  const [state, dispatch] = useReducer(reducer, initialState)
  /** 供 lodash 防抖函数读取最新 type，避免依赖 state.type 反复重建 debounce */
  const supplierSearchTypeRef = useRef(state.type)
  supplierSearchTypeRef.current = state.type
  const location = useLocation()
  const query = useQuery()
  const dispatchRedux = useDispatch()
  const { onCurrentPageClose } = useCloseTab()

  const { pagekey } = useTypedSelector((s) => s.pageCache)

  /**
   * 供 Form.Item 自定义校验读取「当前搜索词 + 是否精确无结果」
   */
  const supplierSearchMetaRef = useRef({ keyword: '', noExactMatch: false })

  const supplierSearchReqSeqRef = useRef(0)

  /** 当前下拉中展示的项 */
  const [supplierOptions, setSupplierOptions] = useState<
    { label: string; value: string }[]
  >([])
  const [supplierSearchLoading, setSupplierSearchLoading] = useState(false)

  const orderIdFromQuery = query.get('orderId') || ''

  /**
   * 【每次 mount 执行】数据恢复（与 adddeliveryorder 同模式）：
   * 1. 有 location.state.materialRows → 从订单详情刚跳转过来，以本次勾选为准，同时写入 Redux 缓存
   * 2. 无 location.state → 从 Redux pagecache 恢复（切 tab 回来的场景）
   * 3. 仅有 orderId（无行数据）→ 只写入订单头
   */
  useEffect(() => {
    const st = location.state as
      | {
          orderId?: string
          orderNo?: string
          materialRows?: any[]
          type?: string
        }
      | undefined

    if (st?.materialRows?.length) {
      // 从订单详情带物料进来
      const oid = String(orderIdFromQuery || st?.orderId || '')
      const ono = String(st?.orderNo || '')
      const materialList = normalizeMaterialRows(st.materialRows)
      const type = String(st?.type ?? '')

      dispatch(
        reloadStateAction({
          ...initialState,
          orderId: oid,
          orderNo: ono,
          materialList,
          selectedMaterialRowKeys: [],
          type,
        }),
      )
      resetSupplierPickState(form, supplierSearchMetaRef, setSupplierOptions)

      // 写入 Redux 缓存
      dispatchRedux(
        setModuleDataAction({
          key: CACHE_KEY,
          data: {
            orderId: oid,
            orderNo: ono,
            materialList,
            selectedMaterialRowKeys: [],
            type,
          } as OrderTransferCacheData,
        }),
      )
      return
    }

    // 无 location.state → 从 Redux 缓存恢复
    const cached = getItemFromItemListByProperty(pagekey, 'key', CACHE_KEY)
      ?.data as OrderTransferCacheData | undefined

    if (cached?.materialList?.length) {
      dispatch(
        reloadStateAction({
          orderId: cached.orderId || '',
          orderNo: cached.orderNo || '',
          materialList: cached.materialList,
          selectedMaterialRowKeys: cached.selectedMaterialRowKeys ?? [],
          type: cached.type || '',
        }),
      )

      // 恢复供应商表单
      const sup = cached.orderTransferSupplier
      if (sup?.supplierId && form) {
        const label = sup.supplierName ?? ''
        form.setFieldsValue({
          [FORM_SUPPLIER_ID]: sup.supplierId,
          [FORM_SUPPLIER_NAME]: label,
        })
        supplierSearchMetaRef.current = { keyword: '', noExactMatch: false }
        setSupplierOptions([
          { label: label || sup.supplierId, value: sup.supplierId },
        ])
      } else {
        resetSupplierPickState(form, supplierSearchMetaRef, setSupplierOptions)
      }
      return
    }

    // 仅有 orderId
    const oid = String(orderIdFromQuery || st?.orderId || '')
    if (oid) {
      dispatch(setOrderIdAction(oid))
      dispatch(setOrderNoAction(String(st?.orderNo || '')))
      resetSupplierPickState(form, supplierSearchMetaRef, setSupplierOptions)
    }
    // eslint-disable-next-line
  }, [])

  /** 将当前状态 + 供应商写入 Redux 缓存 */
  const persistPageCache = useCallback(() => {
    if (!state.orderId) return
    const orderTransferSupplier = form
      ? {
          supplierId: form.getFieldValue(FORM_SUPPLIER_ID) as
            | string
            | undefined,
          supplierName: form.getFieldValue(FORM_SUPPLIER_NAME) as
            | string
            | undefined,
        }
      : undefined
    dispatchRedux(
      setModuleDataAction({
        key: CACHE_KEY,
        data: {
          orderId: state.orderId,
          orderNo: state.orderNo,
          materialList: state.materialList,
          selectedMaterialRowKeys: state.selectedMaterialRowKeys,
          type: state.type,
          orderTransferSupplier,
        } as OrderTransferCacheData,
      }),
    )
  }, [dispatchRedux, form, state])

  /** state 变化时同步到 Redux 缓存，确保切 tab 后能恢复 */
  useEffect(() => {
    persistPageCache()
  }, [persistPageCache])

  /**
   * Select 搜索：`GET_SUPPLIER_CAN_TRANSFER`（/supplier/findBy）
   * 使用 lodash debounce：连续输入只会在停手后触发一次请求
   */
  const debouncedSupplierSearch = useMemo(
    () =>
      debounce((keyword: string) => {
        const t = keyword.trim()
        if (!t) return

        const seq = ++supplierSearchReqSeqRef.current
        setSupplierSearchLoading(true)
        void (async () => {
          try {
            const res = await GET_SUPPLIER_CAN_TRANSFER({
              name: t,
              type: supplierSearchTypeRef.current,
            })
            if (seq !== supplierSearchReqSeqRef.current) return
            if (!isResponseCodeSuccess(res.code)) {
              setSupplierOptions([])
              supplierSearchMetaRef.current.noExactMatch = true
              return
            }
            const raw = normalizeSupplierFindByList(res.data)
            const mapped = raw
              .map((row) => mapSupplierFindByRow(row))
              .filter(Boolean) as { label: string; value: string }[]
            setSupplierOptions(mapped)
            supplierSearchMetaRef.current.noExactMatch = mapped.length === 0
          } catch {
            if (seq !== supplierSearchReqSeqRef.current) return
            setSupplierOptions([])
            supplierSearchMetaRef.current.noExactMatch = true
          } finally {
            if (seq === supplierSearchReqSeqRef.current) {
              setSupplierSearchLoading(false)
            }
          }
        })()
      }, SUPPLIER_SEARCH_DEBOUNCE_MS),
    [],
  )

  useEffect(() => {
    return () => {
      debouncedSupplierSearch.cancel()
    }
  }, [debouncedSupplierSearch])

  const onSupplierSearch = useCallback(
    (keyword: string) => {
      supplierSearchMetaRef.current.keyword = keyword
      const t = keyword.trim()
      if (!t) {
        debouncedSupplierSearch.cancel()
        supplierSearchReqSeqRef.current += 1
        supplierSearchMetaRef.current.noExactMatch = false
        setSupplierOptions([])
        setSupplierSearchLoading(false)
        return
      }
      debouncedSupplierSearch(keyword)
    },
    [debouncedSupplierSearch],
  )

  /** 选中 / 清除：同步隐藏 supplierName，并清理「无精确匹配」标记 */
  const onTargetSupplierChange = useCallback(
    (value?: string) => {
      const id = value ? String(value) : undefined
      const opt = supplierOptions.find((o) => o.value === id)
      form?.setFieldsValue({
        [FORM_SUPPLIER_NAME]: opt?.label ?? '',
      })
      if (!id) {
        supplierSearchMetaRef.current = { keyword: '', noExactMatch: false }
        setSupplierOptions([])
      } else {
        supplierSearchMetaRef.current.noExactMatch = false
      }
      persistPageCache()
    },
    [form, persistPageCache, supplierOptions],
  )

  /** Form 校验规则 */
  const supplierFormRules = useMemo<Rule[]>(
    () => [
      {
        validator: async (_rule, value?: string) => {
          if (value) {
            return Promise.resolve()
          }
          const { keyword, noExactMatch } = supplierSearchMetaRef.current
          if (keyword.trim() && noExactMatch) {
            return Promise.reject(
              new Error('未找到匹配的供应商，请输入准确的全称'),
            )
          }
          return Promise.reject(new Error('请输入供应商全称'))
        },
      },
    ],
    [],
  )

  const onMaterialRowSelectChange = (keys: React.Key[]) => {
    dispatch(setSelectedMaterialRowKeysAction(keys))
  }

  const deleteRowsDisabled = state.selectedMaterialRowKeys.length === 0

  const handleDeleteMaterialRows = () => {
    if (state.selectedMaterialRowKeys.length === 0) {
      return
    }
    const keySet = new Set(state.selectedMaterialRowKeys.map(String))
    const remaining = state.materialList.filter(
      (r) => !keySet.has(String(r.id)),
    )
    if (remaining.length === 0) {
      message.error('至少保留一行！')
      return
    }
    dispatch(setMaterialListAction(remaining))
    dispatch(setSelectedMaterialRowKeysAction([]))
  }

  const handleCancel = () => {
    onCurrentPageClose()
  }

  /**
   * 确认转派：Form 校验供应商 → 校验物料表 → 提交接口
   */
  const handleConfirmTransfer = async () => {
    if (!form) {
      message.warning('页面未就绪')
      return
    }
    try {
      await form.validateFields([FORM_SUPPLIER_ID])
    } catch {
      return
    }
    if (state.materialList.length === 0) {
      message.warning('转派物料明细不能为空')
      return
    }
    const supplierId = String(form.getFieldValue(FORM_SUPPLIER_ID) ?? '')
    const supplierName = String(form.getFieldValue(FORM_SUPPLIER_NAME) ?? '')
    if (!supplierId) {
      message.warning('请选择转派供应商')
      return
    }
    try {
      const res = await POST_TRANSFER_DISPATCH({
        orderId: state.orderId,
        supplierId,
        supplierName,
        detailList: state.materialList.map((item) => ({
          detailId: item.id,
          num: item.transferableQty,
        })),
      })
      if (isResponseCodeSuccess(res.code)) {
        message.success(res.msg || '操作成功')
        onCurrentPageClose(() => {
          dispatchRedux(delModuleDataAction(CACHE_KEY))
        })
      }
    } catch {
      message.error('转派请求失败')
    }
  }

  return {
    state,
    supplierOptions,
    supplierSearchLoading,
    onSupplierSearch,
    onTargetSupplierChange,
    supplierFormRules,
    onMaterialRowSelectChange,
    deleteRowsDisabled,
    handleDeleteMaterialRows,
    handleCancel,
    handleConfirmTransfer,
    resetPage: () => dispatch(resetStateAction()),
  }
}
/**
 * 订单转派页 — 业务逻辑
 *
 * 【路由入参】
 * - URL 查询：`?orderId=xxx`（与订单详情跳转时保持一致，便于刷新后仍知订单）
 * - `location.state`：订单详情 `history.push` 传入 `{ orderId, orderNo, materialRows, type }`
 *
 * 【缓存方案】（与 adddeliveryorder 一致）
 * - 不使用 KeepAlive（路由无 cache: true）
 * - 使用 Redux pagecache 持久化页面状态
 * - 有 location.state → 使用并写入 Redux；无 location.state → 从 Redux 恢复
 *
 * 【供应商表单项】
 * - 由页面 `Form` 托管 `supplierId`、`supplierName`；提交接口从表单取值
 * - 校验文案：空 →「请输入供应商全称」；有搜索无精确命中 →「未找到匹配的供应商，请输入准确的全称」
 *
 * 【可转派数量】
 * - 公式：数量(num) − 取消数(cancelCount) − 退货数(reCount)，与订单详情页一致
 */
import { message } from 'antd'
import type { FormInstance } from 'antd/es/form'
import type { Rule } from 'antd/es/form'
import type { MutableRefObject } from 'react'
import {
  useCallback,
  useEffect,
  useReducer,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useDispatch } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { useTypedSelector } from '@/redux/store'
import { delModuleDataAction, setModuleDataAction } from '@/modules/pagecache'
import { useCloseTab, useQuery } from '@/tools/utilHooks'
import {
  getItemFromItemListByProperty,
  isResponseCodeSuccess,
} from '@/tools/util'
import reducer, {
  initialState,
  reloadStateAction,
  resetStateAction,
  setMaterialListAction,
  setOrderIdAction,
  setOrderNoAction,
  setSelectedMaterialRowKeysAction,
  TransferMaterialRow,
} from './reducer'
import {
  GET_SUPPLIER_CAN_TRANSFER,
  POST_TRANSFER_DISPATCH,
  type SupplierFindByDTO,
} from '@/api/orderManage'
import { debounce } from 'lodash'

// ---------- 纯函数：可转派数量 = 数量(num) − 取消数(cancelCount) − 退货数(reCount) ----------

/** 可转派数量 = num − cancelCount − reCount（与订单详情 hooks 一致） */
function getProductTransferRemainQty(row: {
  num?: number
  cancelCount?: number
  reCount?: number
}): number {
  const n = Number(row?.num ?? 0)
  const cancel = Number(row?.cancelCount ?? 0)
  const ret = Number(row?.reCount ?? 0)
  if ([n, cancel, ret].some((v) => Number.isNaN(v))) {
    return 0
  }
  return n - cancel - ret
}

/**
 * 将详情页勾选行 / 缓存行转为表格行。
 * 可转派数量默认 = num − cancelCount − reCount；旧缓存仅有 transferableQty 且无三字段时沿用该值。
 */
function normalizeMaterialRows(rows: any[]): TransferMaterialRow[] {
  return (rows || []).map((row) => {
    const num =
      row?.num !== undefined && row?.num !== null ? Number(row.num) : undefined
    const cancelCount =
      row?.cancelCount !== undefined && row?.cancelCount !== null
        ? Number(row.cancelCount)
        : undefined
    const reCount =
      row?.reCount !== undefined && row?.reCount !== null
        ? Number(row.reCount)
        : undefined
    const hasLineQty =
      num !== undefined || cancelCount !== undefined || reCount !== undefined
    const transferableQty = hasLineQty
      ? getProductTransferRemainQty({ num, cancelCount, reCount })
      : row?.transferableQty != null
        ? Number(row.transferableQty)
        : 0
    return {
      id: String(row?.id ?? ''),
      code: String(row?.code ?? ''),
      platformMaterialCode: row?.platformMaterialCode,
      systemCategoryCode: row?.systemCategoryCode,
      brand: row?.brand,
      name: row?.name,
      model: row?.model,
      unit: row?.unit,
      num,
      cancelCount,
      reCount,
      transferableQty,
    }
  })
}

const FORM_SUPPLIER_ID = 'supplierId'
const FORM_SUPPLIER_NAME = 'supplierName'

/** Redux 页缓存 key */
const CACHE_KEY = 'ordertransfertemp'

/** 供应商 Select 远程搜索防抖（ms），减轻 /supplier/findBy 压力 */
const SUPPLIER_SEARCH_DEBOUNCE_MS = 500

/** Redux 页缓存里与本页对应的 data 形状 */
type OrderTransferCacheData = {
  orderId?: string
  orderNo?: string
  materialList?: TransferMaterialRow[]
  selectedMaterialRowKeys?: React.Key[]
  type?: string
  orderTransferSupplier?: { supplierId?: string; supplierName?: string }
}

/** 清空表单与搜索态 */
function resetSupplierPickState(
  f: FormInstance | undefined,
  metaRef: MutableRefObject<{ keyword: string; noExactMatch: boolean }>,
  setOptions: (o: { label: string; value: string }[]) => void,
) {
  f?.setFieldsValue({
    [FORM_SUPPLIER_ID]: undefined,
    [FORM_SUPPLIER_NAME]: '',
  })
  metaRef.current = { keyword: '', noExactMatch: false }
  setOptions([])
}

function mapSupplierFindByRow(
  item: SupplierFindByDTO,
): { label: string; value: string } | null {
  const value = String(item.id ?? '').trim()
  const label = String(item.name ?? '').trim()
  if (!value || !label) return null
  return { label, value }
}

/** 接口 `data` 可能是单条 `{ id, name }` 或 `SupplierFindByDTO[]` */
function normalizeSupplierFindByList(data: unknown): SupplierFindByDTO[] {
  if (data == null) return []
  if (Array.isArray(data)) return data as SupplierFindByDTO[]
  if (typeof data === 'object' && data !== null) {
    const o = data as Record<string, unknown>
    if ('id' in o && 'name' in o) {
      return [data as SupplierFindByDTO]
    }
  }
  return []
}

// ---------- Hook 主体 ----------

export default function useOrderTransferHook(form?: FormInstance) {
  const [state, dispatch] = useReducer(reducer, initialState)
  /** 供 lodash 防抖函数读取最新 type，避免依赖 state.type 反复重建 debounce */
  const supplierSearchTypeRef = useRef(state.type)
  supplierSearchTypeRef.current = state.type
  const location = useLocation()
  const query = useQuery()
  const dispatchRedux = useDispatch()
  const { onCurrentPageClose } = useCloseTab()

  const { pagekey } = useTypedSelector((s) => s.pageCache)

  /**
   * 供 Form.Item 自定义校验读取「当前搜索词 + 是否精确无结果」
   */
  const supplierSearchMetaRef = useRef({ keyword: '', noExactMatch: false })

  const supplierSearchReqSeqRef = useRef(0)

  /** 当前下拉中展示的项 */
  const [supplierOptions, setSupplierOptions] = useState<
    { label: string; value: string }[]
  >([])
  const [supplierSearchLoading, setSupplierSearchLoading] = useState(false)

  const orderIdFromQuery = query.get('orderId') || ''

  /**
   * 【每次 mount 执行】数据恢复（与 adddeliveryorder 同模式）：
   * 1. 有 location.state.materialRows → 从订单详情刚跳转过来，以本次勾选为准，同时写入 Redux 缓存
   * 2. 无 location.state → 从 Redux pagecache 恢复（切 tab 回来的场景）
   * 3. 仅有 orderId（无行数据）→ 只写入订单头
   */
  useEffect(() => {
    const st = location.state as
      | {
          orderId?: string
          orderNo?: string
          materialRows?: any[]
          type?: string
        }
      | undefined

    if (st?.materialRows?.length) {
      // 从订单详情带物料进来
      const oid = String(orderIdFromQuery || st?.orderId || '')
      const ono = String(st?.orderNo || '')
      const materialList = normalizeMaterialRows(st.materialRows)
      const type = String(st?.type ?? '')

      dispatch(
        reloadStateAction({
          ...initialState,
          orderId: oid,
          orderNo: ono,
          materialList,
          selectedMaterialRowKeys: [],
          type,
        }),
      )
      resetSupplierPickState(form, supplierSearchMetaRef, setSupplierOptions)

      // 写入 Redux 缓存
      dispatchRedux(
        setModuleDataAction({
          key: CACHE_KEY,
          data: {
            orderId: oid,
            orderNo: ono,
            materialList,
            selectedMaterialRowKeys: [],
            type,
          } as OrderTransferCacheData,
        }),
      )
      return
    }

    // 无 location.state → 从 Redux 缓存恢复
    const cached = getItemFromItemListByProperty(pagekey, 'key', CACHE_KEY)
      ?.data as OrderTransferCacheData | undefined

    if (cached?.materialList?.length) {
      dispatch(
        reloadStateAction({
          orderId: cached.orderId || '',
          orderNo: cached.orderNo || '',
          materialList: cached.materialList,
          selectedMaterialRowKeys: cached.selectedMaterialRowKeys ?? [],
          type: cached.type || '',
        }),
      )

      // 恢复供应商表单
      const sup = cached.orderTransferSupplier
      if (sup?.supplierId && form) {
        const label = sup.supplierName ?? ''
        form.setFieldsValue({
          [FORM_SUPPLIER_ID]: sup.supplierId,
          [FORM_SUPPLIER_NAME]: label,
        })
        supplierSearchMetaRef.current = { keyword: '', noExactMatch: false }
        setSupplierOptions([
          { label: label || sup.supplierId, value: sup.supplierId },
        ])
      } else {
        resetSupplierPickState(form, supplierSearchMetaRef, setSupplierOptions)
      }
      return
    }

    // 仅有 orderId
    const oid = String(orderIdFromQuery || st?.orderId || '')
    if (oid) {
      dispatch(setOrderIdAction(oid))
      dispatch(setOrderNoAction(String(st?.orderNo || '')))
      resetSupplierPickState(form, supplierSearchMetaRef, setSupplierOptions)
    }
    // eslint-disable-next-line
  }, [])

  /** 将当前状态 + 供应商写入 Redux 缓存 */
  const persistPageCache = useCallback(() => {
    if (!state.orderId) return
    const orderTransferSupplier = form
      ? {
          supplierId: form.getFieldValue(FORM_SUPPLIER_ID) as
            | string
            | undefined,
          supplierName: form.getFieldValue(FORM_SUPPLIER_NAME) as
            | string
            | undefined,
        }
      : undefined
    dispatchRedux(
      setModuleDataAction({
        key: CACHE_KEY,
        data: {
          orderId: state.orderId,
          orderNo: state.orderNo,
          materialList: state.materialList,
          selectedMaterialRowKeys: state.selectedMaterialRowKeys,
          type: state.type,
          orderTransferSupplier,
        } as OrderTransferCacheData,
      }),
    )
  }, [dispatchRedux, form, state])

  /** state 变化时同步到 Redux 缓存，确保切 tab 后能恢复 */
  useEffect(() => {
    persistPageCache()
  }, [persistPageCache])

  /**
   * Select 搜索：`GET_SUPPLIER_CAN_TRANSFER`（/supplier/findBy）
   * 使用 lodash debounce：连续输入只会在停手后触发一次请求
   */
  const debouncedSupplierSearch = useMemo(
    () =>
      debounce((keyword: string) => {
        const t = keyword.trim()
        if (!t) return

        const seq = ++supplierSearchReqSeqRef.current
        setSupplierSearchLoading(true)
        void (async () => {
          try {
            const res = await GET_SUPPLIER_CAN_TRANSFER({
              name: t,
              type: supplierSearchTypeRef.current,
            })
            if (seq !== supplierSearchReqSeqRef.current) return
            if (!isResponseCodeSuccess(res.code)) {
              setSupplierOptions([])
              supplierSearchMetaRef.current.noExactMatch = true
              return
            }
            const raw = normalizeSupplierFindByList(res.data)
            const mapped = raw
              .map((row) => mapSupplierFindByRow(row))
              .filter(Boolean) as { label: string; value: string }[]
            setSupplierOptions(mapped)
            supplierSearchMetaRef.current.noExactMatch = mapped.length === 0
          } catch {
            if (seq !== supplierSearchReqSeqRef.current) return
            setSupplierOptions([])
            supplierSearchMetaRef.current.noExactMatch = true
          } finally {
            if (seq === supplierSearchReqSeqRef.current) {
              setSupplierSearchLoading(false)
            }
          }
        })()
      }, SUPPLIER_SEARCH_DEBOUNCE_MS),
    [],
  )

  useEffect(() => {
    return () => {
      debouncedSupplierSearch.cancel()
    }
  }, [debouncedSupplierSearch])

  const onSupplierSearch = useCallback(
    (keyword: string) => {
      supplierSearchMetaRef.current.keyword = keyword
      const t = keyword.trim()
      if (!t) {
        debouncedSupplierSearch.cancel()
        supplierSearchReqSeqRef.current += 1
        supplierSearchMetaRef.current.noExactMatch = false
        setSupplierOptions([])
        setSupplierSearchLoading(false)
        return
      }
      debouncedSupplierSearch(keyword)
    },
    [debouncedSupplierSearch],
  )

  /** 选中 / 清除：同步隐藏 supplierName，并清理「无精确匹配」标记 */
  const onTargetSupplierChange = useCallback(
    (value?: string) => {
      const id = value ? String(value) : undefined
      const opt = supplierOptions.find((o) => o.value === id)
      form?.setFieldsValue({
        [FORM_SUPPLIER_NAME]: opt?.label ?? '',
      })
      if (!id) {
        supplierSearchMetaRef.current = { keyword: '', noExactMatch: false }
        setSupplierOptions([])
      } else {
        supplierSearchMetaRef.current.noExactMatch = false
      }
      persistPageCache()
    },
    [form, persistPageCache, supplierOptions],
  )

  /** Form 校验规则 */
  const supplierFormRules = useMemo<Rule[]>(
    () => [
      {
        validator: async (_rule, value?: string) => {
          if (value) {
            return Promise.resolve()
          }
          const { keyword, noExactMatch } = supplierSearchMetaRef.current
          if (keyword.trim() && noExactMatch) {
            return Promise.reject(
              new Error('未找到匹配的供应商，请输入准确的全称'),
            )
          }
          return Promise.reject(new Error('请输入供应商全称'))
        },
      },
    ],
    [],
  )

  const onMaterialRowSelectChange = (keys: React.Key[]) => {
    dispatch(setSelectedMaterialRowKeysAction(keys))
  }

  const deleteRowsDisabled = state.selectedMaterialRowKeys.length === 0

  const handleDeleteMaterialRows = () => {
    if (state.selectedMaterialRowKeys.length === 0) {
      return
    }
    const keySet = new Set(state.selectedMaterialRowKeys.map(String))
    const remaining = state.materialList.filter(
      (r) => !keySet.has(String(r.id)),
    )
    if (remaining.length === 0) {
      message.error('至少保留一行！')
      return
    }
    dispatch(setMaterialListAction(remaining))
    dispatch(setSelectedMaterialRowKeysAction([]))
  }

  const handleCancel = () => {
    onCurrentPageClose()
  }

  /**
   * 确认转派：Form 校验供应商 → 校验物料表 → 提交接口
   */
  const handleConfirmTransfer = async () => {
    if (!form) {
      message.warning('页面未就绪')
      return
    }
    try {
      await form.validateFields([FORM_SUPPLIER_ID])
    } catch {
      return
    }
    if (state.materialList.length === 0) {
      message.warning('转派物料明细不能为空')
      return
    }
    const supplierId = String(form.getFieldValue(FORM_SUPPLIER_ID) ?? '')
    const supplierName = String(form.getFieldValue(FORM_SUPPLIER_NAME) ?? '')
    if (!supplierId) {
      message.warning('请选择转派供应商')
      return
    }
    try {
      const res = await POST_TRANSFER_DISPATCH({
        orderId: state.orderId,
        supplierId,
        supplierName,
        detailList: state.materialList.map((item) => ({
          detailId: item.id,
          num: item.transferableQty,
        })),
      })
      if (isResponseCodeSuccess(res.code)) {
        message.success(res.msg || '操作成功')
        onCurrentPageClose(() => {
          dispatchRedux(delModuleDataAction(CACHE_KEY))
        })
      }
    } catch {
      message.error('转派请求失败')
    }
  }

  return {
    state,
    supplierOptions,
    supplierSearchLoading,
    onSupplierSearch,
    onTargetSupplierChange,
    supplierFormRules,
    onMaterialRowSelectChange,
    deleteRowsDisabled,
    handleDeleteMaterialRows,
    handleCancel,
    handleConfirmTransfer,
    resetPage: () => dispatch(resetStateAction()),
  }
}
